import { useState } from 'react';
import styled from 'styled-components';
import ViewerContainer from '../components/main/ViewerContainer';
import CategorySidebar from '../components/main/CategorySidebar';
import OptionsPanel from '../components/main/OptionsPanel';
import ViewsPanel from '../components/main/ViewsPanel';
import { VapState } from '@/types/vap-types';
import DoorOpeningIcon from '../icons/DoorOpeningIcon.svg?react';

interface MainPageProps {
  className?: string;
  vapState: VapState;
}

/**
 * Main layout component for VAP application
 * Three-section layout:
 * - Left Sidebar: Category list (product layers)
 * - Center: Scene viewer
 * - Bottom Panel: Options list (product references for selected category)
 */
const MainPage: React.FC<MainPageProps> = ({ className, vapState }) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isLeftSidebarHidden, setIsLeftSidebarHidden] = useState(false);

  const handleCategorySelect = (category: string) => {
    setSelectedCategory((prev) => prev === category ? null : category);
    setIsLeftSidebarHidden(true);;
  };

  const handleBackButton = () => {
    vapState.resetModel();
    setIsLeftSidebarHidden(true);
  }

  const handleOptionSelect = (productRef: string) => {
    if (selectedCategory) {
      vapState.setProductRefForLayer(selectedCategory, vapState.getSelectedRefForLayer(selectedCategory) === productRef ? "" : productRef);
    }
  };

  // Show loading state
  const LoadingOverlay = () => (
    <LoadingContainer>
      <LoadingText>Loading UGLA file...</LoadingText>
    </LoadingContainer>
  );

  // Show error state
  if (vapState.error) {
    return (
      <Container className={className}>
        <ErrorContainer>
          <ErrorText>Error: {vapState.error}</ErrorText>
        </ErrorContainer>
      </Container>
    );
  }

  // Show empty state if no assets loaded
  if (vapState.availableLayers.length === 0 && vapState.currentView !== "360") {
    return (
      <Container className={className}>
        <EmptyContainer>
          <EmptyText>No UGLA file loaded. Please load a file to begin.</EmptyText>
        </EmptyContainer>
      </Container>
    );
  }

  return (
    <>
      {vapState.isLoading && <LoadingOverlay/>}
      <Container className={className}>
        <MainContent>
          <SidebarToggle onClick={() => setIsLeftSidebarHidden(prev => !prev)}>
            ☰
          </SidebarToggle>
          <SidebarOverlay $isHidden={isLeftSidebarHidden} onClick={() => setIsLeftSidebarHidden(false)}/>
          {
            vapState.currentView !== "360" &&

            <LeftSidebar $isHidden={isLeftSidebarHidden}>
              <CategorySidebar
                categories={vapState.availableLayers}
                selectedCategory={selectedCategory}
                onCategorySelect={handleCategorySelect}
                onBackClick={handleBackButton}
              />
            </LeftSidebar>
          }
          <ViewerPanel>
            <ViewerContainer 
              is360={vapState.currentView === "360"} 
              url360={vapState.current360}
            />
            {
              vapState.currentView !== "360" && 
              <>
              {
                selectedCategory &&
                <BottomPanel>
                    <OptionsPanel
                      category={selectedCategory}
                      options={vapState.getProductRefsForLayer(selectedCategory)}
                      selectedOption={vapState.getSelectedRefForLayer(selectedCategory)}
                      onOptionSelect={handleOptionSelect}
                      getAssetForOption={(ref) =>
                        vapState.getAssetForLayerAndRef(selectedCategory, ref)
                      }
                    />
                </BottomPanel>
                  }
                <OpenAllButton $isBottomPanelOpen={!!selectedCategory}>
                  <OpenAllButtonIcon>
                    <DoorOpeningIcon/>
                  </OpenAllButtonIcon>
                  <OpenAllButtonText>
                    <span>OUVRIR </span>
                    <span>LES MEUBLES</span>
                  </OpenAllButtonText>
                </OpenAllButton>
              </>
            }
          </ViewerPanel>
          <RightSidebar>
              <ViewsPanel
                views={Object.keys(vapState.layerConfiguration)}
                selectedView={vapState.currentView}
                onViewChange={vapState.setCurrentView}
                getAssetForView={vapState.getAssetForView}
              />
          </RightSidebar>
        </MainContent>
      </Container>
    </>
  );
};

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background-color: #fff;
`;

const LeftSidebar = styled.div<{ $isHidden?: boolean }>`
  // min-width: 200px;
  width: 20%;
  height: 100%;
  background: ${props => props.theme.gradients.leftSideBarBackground};
  overflow-y: auto;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  transition: transform 0.3s ease, box-shadow 0.3s;
  z-index: 1300;

  @media (max-width: ${props => props.theme.breakpoints?.tablet}) {
    position: absolute;
    top: 0;
    left: 0;
    width: 70vw;
    max-width: 320px;
    transform: ${({ $isHidden }) => !$isHidden ? 'translateX(0)' : 'translateX(-100%)'};
    box-shadow: ${({ $isHidden }) => !$isHidden ? '2px 0 16px rgba(0,0,0,0.2)' : 'none'};
    border-radius: 0 10px 10px 0;
`;

const SidebarToggle = styled.button`
  display: none;
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 1400;
  background: rgba(0, 0, 0, 0.4);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  color: #fff;
  font-size: 2rem;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    background: rgba(0, 0, 0, 0.6);
  }

  @media (max-width: ${props => props.theme.breakpoints?.tablet}) {
    display: flex;
  }
`

const SidebarOverlay = styled.div<{ $isHidden?: boolean }>`
  display: none;

  @media (max-width: ${props => props.theme.breakpoints?.tablet}) {
    display: ${({ $isHidden }) => !$isHidden ? 'block' : 'none'};
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    z-index: 1200;
    background: transparent;
    pointer-events: none;
  }
`

const MainContent = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: #fff;
  position: relative;
`;

const ViewerPanel = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #eee;
  border-radius: 10px;
  margin-left: 1vw;
  position: relative;
`

const OpenAllButton = styled.button<{ $isBottomPanelOpen: boolean }>`
  position: absolute;
  bottom: ${({$isBottomPanelOpen}) => $isBottomPanelOpen ? "20%" : "1vh"};
  right: 10px;
  background: rgba(255, 255, 255, .5);
  border-radius: 30px;
  display: flex;
  align-items: center;
  border: none;
  transition: background .3s ease-in-out;
  &:hover{
    cursor: pointer;
    background: rgba(255, 255, 255, 1);
  }
`

const OpenAllButtonIcon = styled.div`
  padding: 0 .5vw;
`

const OpenAllButtonText = styled.div`
  font-family: Poppins-Bold, sans-serif;
  font-size: 14px;
  color: black;
  text-align: left;
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  padding-right: .5vw;
`

const BottomPanel = styled.div`
  width: 100%;
  height: 20%;
  max-height: 180px;
  background-color: ${props => props.theme.colors.bottomPanelBackground};
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
`;

const RightSidebar = styled.div`
  display: flex;
  position: relative;
  top: 0;
  right: 0;
  height: 70%;
`

const LoadingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  z-index: 200;
  background: #fff;
`;

const LoadingText = styled.div`
  font-size: 1.5rem;
  color: #666;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  background-color: #fee;
`;

const ErrorText = styled.div`
  font-size: 1.2rem;
  color: #c00;
  padding: 2rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
`;

const EmptyText = styled.div`
  font-size: 1.2rem;
  color: #666;
  padding: 2rem;
  background-color: #f5f5f5;
  border-radius: 8px;
`;

export default MainPage;
